import { useEffect, useState } from "react";
import { FileText, X, Loader2, Gavel } from "lucide-react";
import api from "@/lib/api";
import { DEMO_LAWS } from "./mdtDemo";
import { inputCls, btnCls } from "./RosterTab";
import { jailLabel } from "./LawsTab";

/* Closing report on a call — narrative + optional suspect & charges from the penal
   code. Filing with charges books the suspect straight onto the courthouse docket
   and closes the CAD alert. */

export default function ReportForm({ serverId, token, demo, alert, onDone, onCancel }) {
  const [narrative, setNarrative] = useState("");
  const [suspectPc, setSuspectPc] = useState("");
  const [suspectName, setSuspectName] = useState("");
  const [laws, setLaws] = useState([]);
  const [charges, setCharges] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (demo) { setLaws(DEMO_LAWS); return; }
    api.get(`/nc/servers/${serverId}/cad/penal`, { params: { token } }).then((r) => setLaws(r.data.laws)).catch(() => {});
  }, [demo, serverId, token]);

  const picked = laws.filter((l) => charges.includes(l.id));
  const totals = { fine: picked.reduce((a, l) => a + l.fine, 0), jail: picked.reduce((a, l) => a + l.jail_seconds, 0), points: picked.reduce((a, l) => a + l.points, 0) };

  const file = async () => {
    if (narrative.trim().length < 10) return;
    setBusy(true);
    if (demo) {
      import("sonner").then(({ toast }) => toast.success(`Report filed on ${alert.ref}` + (charges.length && suspectPc ? " — suspect booked, docket CRT-26-002" : "")));
      onDone(); return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/alerts/${alert.id}/report`, {
        narrative: narrative.trim(), suspect_playercode: +suspectPc || 0,
        suspect_name: suspectName.trim(), charge_ids: charges, close_alert: true,
      }, { params: { token } });
      import("sonner").then(({ toast }) => toast.success(r.data.message));
      onDone();
    } catch (e) {
      import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Report failed"));
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="report-modal">
      <div className="w-full max-w-lg border border-white/15 bg-[#0a0d12] p-4 flex flex-col gap-3 max-h-full overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-sky-400" />
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-100">Closing report — {alert.ref}</p>
          <button data-testid="report-cancel-x" onClick={onCancel} className="ml-auto text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
        </div>
        <p className="font-mono text-[9px] text-zinc-500 leading-snug border-l-2 border-sky-500/40 pl-2">{alert.summary}</p>
        <textarea data-testid="report-narrative" value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={4}
          placeholder="Narrative — what happened, actions taken, evidence recovered (min 10 chars)…" className={`${inputCls} w-full resize-none`} />
        <div className="flex gap-1.5">
          <input data-testid="report-suspect-pc" value={suspectPc} onChange={(e) => setSuspectPc(e.target.value.replace(/\D/g, ""))} placeholder="Suspect playercode (optional)" className={`${inputCls} w-40`} />
          <input data-testid="report-suspect-name" value={suspectName} onChange={(e) => setSuspectName(e.target.value)} placeholder="Suspect name" className={`${inputCls} flex-1`} />
        </div>
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Charges (from penal code) — with a suspect this files to the courthouse</p>
          <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            {laws.map((l) => (
              <button key={l.id} data-testid={`report-charge-${l.code}`} onClick={() => setCharges((c) => c.includes(l.id) ? c.filter((x) => x !== l.id) : [...c, l.id])}
                className={`text-left px-2 py-1.5 border font-mono text-[8.5px] transition-colors ${charges.includes(l.id) ? "border-red-400 text-red-300 bg-red-500/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                <span className="font-bold">{l.code}</span> {l.title}
                <span className="block text-[7.5px] text-zinc-500">${l.fine.toLocaleString()} · {jailLabel(l.jail_seconds)} · {l.points}pts</span>
              </button>
            ))}
          </div>
        </div>
        {picked.length > 0 && (
          <p className="font-mono text-[9px] text-zinc-300">
            Totals: <span className="text-emerald-300 font-bold">${totals.fine.toLocaleString()}</span> fine · <span className="text-orange-300 font-bold">{jailLabel(totals.jail)}</span> jail · <span className="text-sky-300 font-bold">{totals.points}</span> pts
            {suspectPc && <span className="text-red-300 flex items-center gap-1 mt-0.5"><Gavel className="w-3 h-3" /> Will book suspect onto the courthouse docket</span>}
          </p>
        )}
        <div className="flex gap-2">
          <button data-testid="report-file" onClick={file} disabled={busy || narrative.trim().length < 10}
            className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1`}>
            {busy && <Loader2 className="w-3 h-3 animate-spin" />} File report & close call
          </button>
          <button data-testid="report-cancel" onClick={onCancel} className={`${btnCls} border-white/15 text-zinc-400`}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
